import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { api } from "@/lib/api";
import {
  ArrowLeft, Search, Download, Mail, Phone, Building2, GraduationCap,
  Users, Calendar, ChevronDown, ChevronUp, Loader2,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_OPTIONS = ["new", "contacted", "confirmed", "attended", "no_show", "cancelled"];
const STATUS_COLORS = {
  new: "bg-red-100 text-red-700",
  contacted: "bg-amber-100 text-amber-700",
  confirmed: "bg-green-100 text-green-700",
  attended: "bg-blue-100 text-blue-700",
  no_show: "bg-zinc-200 text-zinc-600",
  cancelled: "bg-zinc-200 text-zinc-500",
};

export default function InstructorSeminars() {
  const [data, setData] = useState({ signups: [], by_slot: {}, by_status: {}, upcoming_slots: [] });
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [slotFilter, setSlotFilter] = useState("all");
  const [expanded, setExpanded] = useState(null);
  const [savingId, setSavingId] = useState(null);

  const load = async () => {
    try {
      const { data: d } = await api.get("/admin/seminars?limit=1000");
      setData(d);
    } catch {
      toast.error("Could not load seminar signups.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let rows = data.signups || [];
    if (statusFilter !== "all") rows = rows.filter((r) => (r.status || "new") === statusFilter);
    if (slotFilter !== "all") rows = rows.filter((r) => r.slot_date === slotFilter);
    const q = query.trim().toLowerCase();
    if (q) {
      rows = rows.filter((r) =>
        (r.email || "").toLowerCase().includes(q) ||
        (r.full_name || "").toLowerCase().includes(q) ||
        (r.organization || "").toLowerCase().includes(q),
      );
    }
    return rows;
  }, [data, query, statusFilter, slotFilter]);

  const updateStatus = async (signupId, status) => {
    setSavingId(signupId);
    try {
      await api.patch(`/admin/seminars/${signupId}`, { status });
      setData((d) => ({
        ...d,
        signups: d.signups.map((s) => (s.id === signupId ? { ...s, status } : s)),
      }));
      toast.success(`Marked as ${status.replace("_", " ")}`);
    } catch {
      toast.error("Failed to update status");
    } finally {
      setSavingId(null);
    }
  };

  const downloadCsv = () => {
    if (filtered.length === 0) {
      toast.error("Nothing to export");
      return;
    }
    const headers = ["created_at", "slot_date", "status", "full_name", "email", "phone", "role", "organization", "role_title", "num_students", "notes"];
    const rows = filtered.map((r) => [
      r.created_at || "", r.slot_date || "", r.status || "new",
      r.full_name || "", r.email || "", r.phone || "", r.role || "",
      r.organization || "", r.role_title || "", r.num_students || "",
      (r.notes || "").replace(/\n/g, " ").replace(/"/g, '""'),
    ]);
    const csv = [headers, ...rows]
      .map((row) => row.map((cell) => {
        const s = String(cell);
        return s.includes(",") || s.includes('"') || s.includes("\n") ? `"${s.replace(/"/g, '""')}"` : s;
      }).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `apm-seminar-signups-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} rows`);
  };

  const fmt = (iso) => iso
    ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
    : "—";

  const totalSignups = (data.signups || []).length;
  const parentCount = (data.signups || []).filter((s) => s.role === "parent").length;
  const studentCount = (data.signups || []).filter((s) => s.role === "student").length;
  const uniqueOrgs = new Set((data.signups || []).map((s) => (s.organization || "").toLowerCase()).filter(Boolean)).size;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-12" data-testid="instructor-seminars-page">
        <Link to="/instructor" className="text-sm text-zinc-600 hover:text-red-600 inline-flex items-center gap-1 mb-6" data-testid="seminars-back-link">
          <ArrowLeft className="w-4 h-4" /> Cohort dashboard
        </Link>

        <div className="overline mb-2"><span className="red-bar" />Instructor tools</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter mb-2" data-testid="seminars-title">
          Free Career Seminar
        </h1>
        <p className="text-zinc-600 mb-8 max-w-2xl text-sm leading-relaxed">
          Every Friday 6 PM ET. Track parent, student, and partner-school signups. Update status as you work through the pipeline.
        </p>

        <div className="grid sm:grid-cols-4 gap-3 mb-8">
          <StatCard label="Total signups" value={totalSignups} icon={Calendar} testid="seminars-stat-total" />
          <StatCard label="Parents" value={parentCount} icon={Users} testid="seminars-stat-parents" />
          <StatCard label="Students" value={studentCount} icon={GraduationCap} testid="seminars-stat-students" />
          <StatCard label="Unique orgs" value={uniqueOrgs} icon={Building2} testid="seminars-stat-orgs" />
        </div>

        {/* Upcoming Fridays overview */}
        {(data.upcoming_slots || []).length > 0 && (
          <div className="card-tech p-5 mb-8" data-testid="seminars-upcoming">
            <div className="overline mb-3"><span className="red-bar" />Next 8 Fridays</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {data.upcoming_slots.map((s) => {
                const count = data.by_slot?.[s.date] || 0;
                const active = slotFilter === s.date;
                return (
                  <button
                    key={s.date}
                    onClick={() => setSlotFilter(active ? "all" : s.date)}
                    className={`text-left border-2 px-3 py-2 transition-colors ${active ? "border-red-600 bg-red-50" : "border-zinc-200 hover:border-red-300"}`}
                    data-testid={`seminars-slot-tile-${s.date}`}
                  >
                    <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{s.label.split(" · ")[0]}</div>
                    <div className="font-heading text-2xl font-bold tracking-tight mt-0.5">{count}</div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">signups</div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="flex" data-testid="seminars-status-tabs">
            <button
              onClick={() => setStatusFilter("all")}
              className={`px-3 py-2 text-xs font-mono uppercase tracking-widest border-2 transition-colors ${statusFilter === "all" ? "bg-zinc-950 text-white border-zinc-950" : "bg-white text-zinc-700 border-zinc-200 hover:border-red-300"}`}
              data-testid="seminars-status-all"
            >
              All ({totalSignups})
            </button>
            {STATUS_OPTIONS.map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-2 text-xs font-mono uppercase tracking-widest border-2 border-l-0 transition-colors ${statusFilter === s ? "bg-zinc-950 text-white border-zinc-950" : "bg-white text-zinc-700 border-zinc-200 hover:border-red-300"}`}
                data-testid={`seminars-status-${s}`}
              >
                {s.replace("_", " ")} ({data.by_status?.[s] || 0})
              </button>
            ))}
          </div>
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute top-3 left-3 text-zinc-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, email, organization…"
              className="w-full border-2 border-zinc-200 focus:border-red-600 outline-none pl-9 pr-3 py-2 text-sm bg-white"
              data-testid="seminars-search-input"
            />
          </div>
          <button onClick={downloadCsv} className="btn-outline" data-testid="seminars-csv-btn">
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>

        {slotFilter !== "all" && (
          <div className="text-xs text-zinc-500 mb-3">
            Filtering by slot <span className="font-mono text-zinc-700">{slotFilter}</span> ·{" "}
            <button className="text-red-600 hover:underline" onClick={() => setSlotFilter("all")} data-testid="seminars-clear-slot">clear</button>
          </div>
        )}

        {loading ? (
          <div className="text-sm text-zinc-500 italic py-12 text-center" data-testid="seminars-loading">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="text-sm text-zinc-500 italic py-12 text-center border-2 border-dashed border-zinc-200" data-testid="seminars-empty">
            {query || statusFilter !== "all" || slotFilter !== "all"
              ? "No matches. Clear filters to see everything."
              : "No signups yet. Share the /free-seminar link with your network to fill this pipeline."}
          </div>
        ) : (
          <div className="border border-zinc-200 overflow-x-auto" data-testid="seminars-table">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr>
                  <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-600">Slot</th>
                  <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-600">Name</th>
                  <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-600">Role</th>
                  <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-600">Organization</th>
                  <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-600">Contact</th>
                  <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-600">Status</th>
                  <th className="text-right px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-600">Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row, idx) => {
                  const isExpanded = expanded === row.id;
                  return (
                    <SignupRow
                      key={row.id}
                      row={row}
                      idx={idx}
                      isExpanded={isExpanded}
                      onToggle={() => setExpanded(isExpanded ? null : row.id)}
                      onStatusChange={(s) => updateStatus(row.id, s)}
                      isSaving={savingId === row.id}
                      fmt={fmt}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <div className="card-tech p-4" data-testid={testid}>
      <Icon className="w-5 h-5 text-red-600 mb-2" />
      <div className="font-heading text-3xl font-bold tracking-tight">{value}</div>
      <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mt-1">{label}</div>
    </div>
  );
}

function SignupRow({ row, idx, isExpanded, onToggle, onStatusChange, isSaving, fmt }) {
  const status = row.status || "new";
  return (
    <>
      <tr
        className={`border-b border-zinc-100 last:border-b-0 ${idx % 2 ? "bg-zinc-50/30" : ""} hover:bg-red-50/30 transition-colors`}
        data-testid={`seminars-row-${row.id}`}
      >
        <td className="px-3 py-3 text-xs font-mono whitespace-nowrap">{row.slot_date}</td>
        <td className="px-3 py-3 text-sm font-semibold">{row.full_name}</td>
        <td className="px-3 py-3 text-xs">
          <span className={`px-1.5 py-0.5 font-mono text-[10px] uppercase ${row.role === "parent" ? "bg-blue-100 text-blue-700" : "bg-purple-100 text-purple-700"}`}>
            {row.role}
          </span>
        </td>
        <td className="px-3 py-3 text-sm">{row.organization}</td>
        <td className="px-3 py-3 text-xs font-mono">
          <a href={`mailto:${row.email}`} className="text-zinc-900 hover:text-red-600 inline-flex items-center gap-1"><Mail className="w-3 h-3" />{row.email}</a>
        </td>
        <td className="px-3 py-3 text-xs">
          <select
            value={status}
            onChange={(e) => onStatusChange(e.target.value)}
            disabled={isSaving}
            className={`px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider border-0 outline-none cursor-pointer ${STATUS_COLORS[status]}`}
            data-testid={`seminars-status-select-${row.id}`}
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>{s.replace("_", " ")}</option>
            ))}
          </select>
        </td>
        <td className="px-3 py-3 text-right">
          <button
            onClick={onToggle}
            className="text-xs font-mono text-zinc-500 hover:text-red-600 transition-colors inline-flex items-center gap-1"
            data-testid={`seminars-detail-toggle-${row.id}`}
          >
            {isSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : isExpanded ? <>Hide <ChevronUp className="w-3 h-3" /></> : <>Detail <ChevronDown className="w-3 h-3" /></>}
          </button>
        </td>
      </tr>
      {isExpanded && (
        <tr className="bg-zinc-50 border-b border-zinc-100" data-testid={`seminars-detail-${row.id}`}>
          <td colSpan={7} className="px-6 py-4">
            <div className="grid sm:grid-cols-3 gap-4 text-sm">
              <DetailField label="Signup ID">{row.id}</DetailField>
              <DetailField label="Signed up">{fmt(row.created_at)}</DetailField>
              <DetailField label="Phone">
                {row.phone ? <a href={`tel:${row.phone}`} className="text-red-600 hover:underline inline-flex items-center gap-1"><Phone className="w-3 h-3" />{row.phone}</a> : <span className="text-zinc-400 italic">—</span>}
              </DetailField>
              <DetailField label="Role / title">{row.role_title || <span className="text-zinc-400 italic">—</span>}</DetailField>
              <DetailField label="# students">{row.num_students || <span className="text-zinc-400 italic">—</span>}</DetailField>
              <DetailField label="Slot">{row.slot_label}</DetailField>
              {row.notes && (
                <div className="sm:col-span-3">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Notes</div>
                  <p className="text-sm bg-white p-3 border-l-2 border-red-600 leading-relaxed">{row.notes}</p>
                </div>
              )}
              <div className="sm:col-span-3 pt-2 border-t border-zinc-200">
                <a
                  href={`mailto:${row.email}?subject=${encodeURIComponent(`APM Fire Alarm — Free Seminar (${row.slot_label})`)}`}
                  className="btn-outline text-xs"
                  data-testid={`seminars-reply-${row.id}`}
                >
                  <Mail className="w-3.5 h-3.5" /> Reply via email
                </a>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function DetailField({ label, children }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">{label}</div>
      <div className="text-sm text-zinc-800 break-all">{children}</div>
    </div>
  );
}
